'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import classnames from 'classnames';
import { nanoid } from 'nanoid';
import { allByType } from 'react-children-by-type';
import { L_ARROW_KEYCODE, U_ARROW_KEYCODE, R_ARROW_KEYCODE, D_ARROW_KEYCODE, Button, } from '../../index.js';
import { TabPanel } from './TabPanel.js';
export const Tabs = (_a) => {
    var { className, children, activeIndex, inverted, onChange, onTabClick, changeTabOnHoverAndFocus, scrollable } = _a, other = __rest(_a, ["className", "children", "activeIndex", "inverted", "onChange", "onTabClick", "changeTabOnHoverAndFocus", "scrollable"]);
    const ref = React.useRef();
    const [activeIndexState, setActiveIndexState] = React.useState(activeIndex ? activeIndex : 0);
    const tabRefs = tabs().map(() => React.createRef());
    const nextRef = React.useRef(null);
    const prevRef = React.useRef(null);
    const [idVar, setId] = React.useState([]);
    const [ariaLabelledByVar, setAriaLabelledBy] = React.useState([]);
    const [offset, setOffset] = React.useState(0);
    const [limit, setLimit] = React.useState(5);
    const [noPrev, setNoPrev] = React.useState(false);
    const [noNext, setNoNext] = React.useState(true);
    const listData = allByType(children, TabPanel);
    const [listItems, setListItems] = React.useState(listData.slice(offset, limit));
    const prev = () => {
        if (offset <= 5) {
            setNoPrev(false);
        }
        setNoNext(true);
        setOffset(offset - 5);
        setLimit(limit - 5);
        setListItems(listData.slice(offset - 5, limit - 5));
    };
    const next = () => {
        if (limit >= listData.length - 5) {
            setNoNext(false);
        }
        setNoPrev(true);
        setOffset(limit);
        setLimit(limit + 5);
        setListItems(listData.slice(offset + 5, limit + 5));
    };
    function tabs() {
        return allByType(children, TabPanel);
    }
    function usePrevious(activeIndex) {
        React.useEffect(() => {
            ref.current = activeIndex;
        });
        return ref.current;
    }
    const prevActiveIndex = usePrevious(activeIndex);
    React.useEffect(() => {
        if ((prevActiveIndex !== undefined || null) &&
            prevActiveIndex !== activeIndex) {
            setActiveIndexState(activeIndex);
            const el = tabRefs[activeIndex].current;
            if (el) {
                el.focus();
            }
        }
    }, [prevActiveIndex, activeIndex, tabRefs]);
    React.useEffect(() => {
        setId(tabs().map((tab) => (tab.props.id ? tab.props.id : nanoid())));
        setAriaLabelledBy(tabs().map((tab) => tab.props.ariaLabelledBy ? tab.props.ariaLabelledBy : nanoid()));
    }, []);
    function onOpen(index) {
        if (activeIndexState === index)
            return;
        setActiveIndexState(index);
        if (onChange) {
            onChange(index);
        }
    }
    function handleTabClick(event, index) {
        if (onTabClick) {
            onTabClick(event, index);
        }
    }
    function onKeyDown(e) {
        let activeTab = null;
        tabRefs.map((item) => {
            if (item.current === document.activeElement) {
                activeTab = item;
            }
            return item;
        });
        if (!activeTab)
            return;
        const index = tabRefs.indexOf(activeTab);
        const next = index === tabRefs.length - 1 ? 0 : index + 1;
        const prev = index === 0 ? tabRefs.length - 1 : index - 1;
        if ([R_ARROW_KEYCODE, D_ARROW_KEYCODE].includes(e.code)) {
            const el = tabRefs[next].current;
            if (el) {
                el.focus();
            }
        }
        else if ([L_ARROW_KEYCODE, U_ARROW_KEYCODE].includes(e.code)) {
            const el = tabRefs[prev].current;
            if (el) {
                el.focus();
            }
        }
    }
    const componentClassName = classnames('sparky-c-tabs', className, {
        'sparky-c-tabs--inverted': inverted === true,
        'sparky-c-tabs--scrollable': scrollable === true,
    });
    const childrenWithProps = React.Children.map(tabs(), (child, i) => {
        if (React.isValidElement(child)) {
            const c = child;
            return React.cloneElement(c, {
                id: idVar[i],
                ariaLabelledBy: ariaLabelledByVar[i],
            });
        }
        return child;
    });
    const scrl = React.useRef(null);
    const [scrollX, setscrollX] = React.useState(0);
    const [scrolEnd, setscrolEnd] = React.useState(false);
    const slide = (shift) => {
        if (scrl.current !== null) {
            scrl.current.scrollLeft += shift;
            setscrollX(scrollX + shift);
            if (Math.floor(scrl.current.scrollWidth - scrl.current.scrollLeft) <=
                scrl.current.offsetWidth) {
                setscrolEnd(true);
            }
            else {
                setscrolEnd(false);
            }
        }
    };
    const scrollCheck = () => {
        if (scrl.current !== null) {
            setscrollX(scrl.current.scrollLeft);
            if (Math.floor(scrl.current.scrollWidth - scrl.current.scrollLeft) <=
                scrl.current.offsetWidth) {
                setscrolEnd(true);
            }
            else {
                setscrolEnd(false);
            }
        }
    };
    const childrenCount = childrenWithProps && childrenWithProps.length;
    if (childrenCount && childrenCount > listItems.length) {
        setListItems(childrenWithProps);
    }
    const isInitialMount = React.useRef(true);
    React.useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        }
        else {
            scrollCheck();
        }
    });
    return (_jsxs("div", Object.assign({ className: componentClassName }, other, { children: [_jsxs("div", Object.assign({ className: "sparky-c-tabs__header" }, { children: [scrollable && noPrev && (_jsx(Button, { circle: true, text: "arrow-prev", iconName: "chevron-left", iconPosition: "before", iconSize: "lg", hideText: true, variant: "primary", className: "sparky-c-tabs__button sparky-c-tabs__prev", onClick: prev })), _jsx("ul", Object.assign({ className: "sparky-c-tabs__list", role: "tablist", ref: scrl, onScroll: scrollCheck }, { children: listItems.map((tab, i) => {
                            const isActive = activeIndexState === i;
                            return (_jsx("li", Object.assign({ className: classnames('sparky-c-tabs__item', {
                                    'sparky-is-active': isActive,
                                    'sparky-c-tabs__item--small': tab.props.subtitle !== null,
                                }), role: "presentation" }, { children: _jsxs("a", Object.assign({ className: "sparky-c-tabs__link", role: "tab", onClick: (e) => {
                                        if (!changeTabOnHoverAndFocus) {
                                            e.preventDefault();
                                            onOpen(i);
                                        }
                                        handleTabClick(e, i);
                                    }, onMouseEnter: () => {
                                        if (changeTabOnHoverAndFocus) {
                                            onOpen(i);
                                        }
                                    }, id: ariaLabelledByVar[i], onKeyDown: onKeyDown, href: tab.props.href || `#${idVar[i]}`, "aria-selected": isActive, tabIndex: isActive ? -1 : 0, "aria-controls": idVar[i], ref: tabRefs[i], "aria-label": tab.props.ariaLabel, onFocus: () => {
                                        if (changeTabOnHoverAndFocus) {
                                            onOpen(i);
                                        }
                                    } }, { children: [tab.props.title, tab.props.subtitle && _jsx("br", {}), tab.props.subtitle && tab.props.subtitle] }), `tab-${i}`) }), `tabs-item-${i}`));
                        }) })), scrollable && noNext && (_jsx(Button, { circle: true, text: "arrow-next", iconName: "chevron-right", iconPosition: "before", iconSize: "lg", hideText: true, variant: "primary", className: "sparky-c-tabs__button sparky-c-tabs__next", onClick: next }))] })), _jsx("div", Object.assign({ className: "sparky-c-tabs__body" }, { children: childrenWithProps && childrenWithProps[activeIndexState] }))] })));
};
//# sourceMappingURL=Tabs.js.map